// The five flat report forms share one authenticated create/replay boundary.
// A positive migration-229 receipt proves actor, stable identity and the
// canonical original request. Constraint names are never acknowledgement.
// Queued answers and photo bytes survive ambiguous, legacy, deleted and
// different-owner collisions until explicit recovery or verified success.
// Network/server failures use the existing bounded retry queue. Deterministic
// failures require review; Storage 409 only permits continuing to the RPC.
// navigator.onLine is advisory; each actual attempt determines its outcome.

import {useCallback, useEffect, useRef, useState} from 'react';

import {sb} from './supabase.js';
import {newClientSubmissionId} from './clientSubmissionId.js';
import {buildRecord, getFormConfig} from './offlineForms.js';
import type {FormPayload, FormRecord} from './offlineForms.js';
import {queuedReportActor, submitQueuedReport, QUEUED_REPORT_CONFLICT} from './queuedReportApi.js';
import {lastAuthenticatedUserId} from './authIdentityMemory.js';
import {preparePhotos, uploadPreparedPhotosSequential, StorageUploadError} from './dailyPhotos.js';
import {photoDigest, queuedReportPhotoMetadata} from './queuedReportPhotos.js';
import {
  enqueueSubmission,
  enqueueSubmissionWithPhotos,
  listQueued,
  listStuck,
  listPhotoBlobsByCsid,
  markSyncing,
  restoreSubmissionQueued,
  acknowledgeQueuedReport,
  getSubmission,
  markFailed,
  markStuckNow,
  recoverStaleSyncing,
  retrySubmission,
  discardSubmission,
  MAX_RETRIES,
} from './offlineQueue.js';
import type {QueuedSubmission} from './offlineQueue.js';
import type {SupabaseClient} from './supabaseTypes.js';

const TICK_INTERVAL_MS = 60_000;

// ----------------------------------------------------------------------------
// The shapes this hook reasons about
// ----------------------------------------------------------------------------

/**
 * What the classifiers actually read off a failure. Deliberately narrower than
 * PostgrestError/StorageError, and every field optional: this hook is reached
 * by rejected fetches (a bare TypeError), by PostgREST `{error}` envelopes, and
 * by supabase-storage errors, and the whole point of the classifiers below is
 * that not one of those guarantees any particular field.
 *
 * `statusCode` is the storage shape's status (a string on StorageApiError);
 * `status` is the PostgREST / StorageUploadError one. Both are read, which is
 * why both are declared.
 */
export type SubmitError = {
  name?: string;
  message?: string;
  code?: string | number;
  status?: string | number;
  statusCode?: string | number;
  /** supabase-storage puts its error kind here, not on `code` */
  error?: string;
};

/** The `{data, error}` envelope both write paths resolve with. */
export type WriteResponse = {data: unknown; error: SubmitError | null};

/** The three terminal states submit() reports. See _runSubmit's header. */
export type SubmitState = 'synced' | 'queued' | 'stuck';

export type SubmitResult = {
  state: SubmitState;
  csid: string;
  id: string;
  record: FormRecord;
  error?: unknown;
};

/** Identity overrides a caller may pin (the offline replay tests do). */
export type SubmitOptions = {
  csid?: string;
  id?: string;
  // Set only after Open has loaded and the operator has reviewed a queued
  // report. This is a new intent; never reuse its original CSID.
  replacesCsid?: string;
  expectedActor?: string;
  assertCurrent?: () => Promise<boolean> | boolean;
};

export type OfflineSubmitApi = {
  submit: (payload: FormPayload, opts?: SubmitOptions) => Promise<SubmitResult>;
  syncNow: () => Promise<void>;
  stuckRows: QueuedSubmission[];
  queuedCount: number;
  refresh: () => Promise<void>;
  retryStuck: (csid: string) => Promise<void>;
  discardStuck: (csid: string) => Promise<void>;
  syncing: boolean;
};

// A non-null, non-array object. Queue entries come back from IndexedDB as
// opaque data, so the fields this hook reads off `entry.record` are re-proved
// rather than assumed.
function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

// Read a message off an unknown thrown value without asserting a shape. Every
// call site below already had the `err && err.message ? err.message :
// String(err)` idiom inline; this is that idiom, once.
function errMessage(err: unknown): string {
  return isPlainRecord(err) && typeof err.message === 'string' ? err.message : String(err);
}

// A unique violation identifies a collision; it cannot prove saved content.
const DUPLICATE_KEY_CODE = '23505';

function isDuplicateCsidViolation(err: SubmitError | null | undefined): boolean {
  if (!err) return false;
  if (String(err.code) !== DUPLICATE_KEY_CODE) return false;
  // Be precise about WHICH constraint — a 23505 on an unrelated index
  // (e.g. cattle.tag) is a real bug, not an idempotency hit.
  const msg = String(err.message || '');
  return /client_submission_id/i.test(msg);
}

// A different active report occupies this date + identity. Retain the draft
// for explicit review, including its photos; never acknowledge this as saved.
function isDailySupersededViolation(err: SubmitError | null | undefined): boolean {
  if (!err) return false;
  if (String(err.code) !== DUPLICATE_KEY_CODE) return false;
  return /_active_daily_identity_uq/i.test(String(err.message || ''));
}

function dailySupersededMessage(record: FormRecord = {}): string {
  const label = record.batch_label || record.herd || record.flock || '';
  const date = record.date || '';
  return label
    ? `A report already exists for ${String(label)} on ${String(date)}. Edit the existing report instead.`
    : `A report already exists for ${String(date)}. Edit the existing report instead.`;
}

// A plain Error carrying the SQLSTATE the classifier keys on. Declared as a
// type rather than an Error subclass so the value stays a real Error (the
// catch paths below rely on `instanceof TypeError` NOT matching it) while the
// added `code` field is still visible to the compiler.
type CodedError = Error & {code?: string; status?: string | number; cause?: unknown};

// Build a schema-class error carrying a friendly message so the live-submit
// catch paths re-raise it to the caller (classifyError keys on the 23 code)
// instead of silently queueing it for a retry that can never succeed.
function dailySupersededError(record: FormRecord): CodedError {
  const e: CodedError = new Error(dailySupersededMessage(record));
  e.code = DUPLICATE_KEY_CODE;
  return e;
}

type ErrorClass = 'network' | 'server' | 'rls' | 'schema' | 'unknown';

function classifyError(err: unknown): ErrorClass {
  // Plain network failure — fetch throws TypeError ("Failed to fetch") on
  // offline / DNS / aborted-connection.
  if (err instanceof TypeError) return 'network';
  // Everything below reads fields off a value that may be an Error, a
  // PostgREST envelope, a storage error, or a string. One narrowing, then the
  // same field reads as before.
  const e = isPlainRecord(err) ? err : null;
  if (e && e.name === 'TypeError') return 'network';
  if (e && typeof e.message === 'string' && /failed to fetch|network ?error|load failed/i.test(e.message)) {
    return 'network';
  }

  // PostgREST error shape — supabase-js v2 returns {message, code, details, hint, status}
  const status = e && e.status != null ? Number(e.status) : null;
  const code = e && e.code != null ? String(e.code) : '';
  if (code === QUEUED_REPORT_CONFLICT || code === 'WCF01') return 'schema';

  if (status != null) {
    if (status >= 500) return 'server';
    if (status === 401 || status === 403) return 'rls';
    // 4xx schema/validation: PGRST116 = no rows, PGRST204 = no schema cache,
    // 23xxx (other than dup-csid handled above) = integrity constraints.
    // These are real bugs the operator can't fix.
    if (status >= 400 && status < 500) {
      if (/^PGRST/i.test(code) || /^23/.test(code) || /^22/.test(code)) return 'schema';
      // Unknown 4xx — queue rather than drop. Operator can discard from the
      // stuck modal if it really is stuck.
      return 'unknown';
    }
  }

  // Codeless schema-class fallback. supabase-js v2 sometimes returns PostgREST
  // error envelopes WITHOUT a `.status` field on the error object (only the
  // response status, which we can't read here). Mirrors useOfflineRpcSubmit's
  // codeless branch — PGRST* / 22* / 23* / P0001 codes always indicate
  // deterministic schema/validation problems regardless of HTTP status.
  if (/^PGRST/i.test(code) || /^23/.test(code) || /^22/.test(code) || code === 'P0001') return 'schema';

  return 'unknown';
}

// ----------------------------------------------------------------------------
// Storage error classifier (Phase 1D-A)
// ----------------------------------------------------------------------------
// supabase-js storage errors don't carry PostgREST shapes. The classifier
// inspects status/code/message of a StorageUploadError (or any storage
// error) and returns one of the offline-queue states.
type StorageErrorClass = 'network' | 'server' | 'success-409' | 'rls-stuck' | 'schema' | 'unknown';

function classifyStorageError(err: unknown): StorageErrorClass {
  if (!err) return 'unknown';
  // Network/fetch/timeout — same logic as the insert classifier's network branch.
  if (err instanceof TypeError) return 'network';
  const e = isPlainRecord(err) ? err : null;
  if (e && e.name === 'TypeError') return 'network';
  if (e && typeof e.message === 'string' && /failed to fetch|network ?error|load failed|timeout/i.test(e.message)) {
    return 'network';
  }
  // A non-object failure carries no status and no code; it lands on the same
  // 'unknown' the field reads below would have produced for it.
  if (!e) return 'unknown';

  // supabase-js storage errors expose the HTTP status as `statusCode`
  // (string) on the native StorageApiError shape, NOT `status`. The hook's
  // StorageUploadError wrapper normalizes `status`, but raw storage errors
  // (e.g. when the queue worker reads error.statusCode directly without
  // the wrapper) still carry only `statusCode`. Fall back to it so the
  // classifier doesn't lose the status on either shape.
  const status = e.status != null ? Number(e.status) : e.statusCode != null ? Number(e.statusCode) : null;
  const code = e.code != null ? String(e.code) : '';

  if (status != null) {
    if (status >= 500) return 'server';
    if (status === 429) return 'server';
    if (status === 409) return 'success-409';
    if (status === 401 || status === 403) return 'rls-stuck';
    if (status >= 400 && status < 500) return 'schema';
  }
  // Codeless duplicate hint (some supabase-js paths drop status).
  if (/duplicate/i.test(code) || /duplicate/i.test(String(e.message))) return 'success-409';
  return 'unknown';
}

// Migration 229 owns the receipt and delegates cattle/sheep creates to their
// existing audited writers. Registry validation keeps the scope at five forms.
async function writeRow(
  formKind: string,
  record: FormRecord,
  actorProfileId: string,
  sbClient: SupabaseClient,
): Promise<WriteResponse> {
  getFormConfig(formKind);
  return submitQueuedReport(sbClient, formKind, record, actorProfileId);
}

async function attemptInsert(formKind: string, record: FormRecord, actorProfileId: string): Promise<WriteResponse> {
  return writeRow(formKind, record, actorProfileId, sb);
}

async function safeReadActor(sbClient: SupabaseClient): Promise<string | null> {
  try {
    return await queuedReportActor(sbClient);
  } catch {
    return null;
  }
}

export function _makeSyncCurrencyGuard(expectedActor: string): () => boolean {
  const initialMemoryActor = lastAuthenticatedUserId();
  return () => {
    const liveMemoryActor = lastAuthenticatedUserId();
    return initialMemoryActor !== null
      ? liveMemoryActor === expectedActor
      : liveMemoryActor === null || liveMemoryActor === expectedActor;
  };
}
const makeSyncCurrencyGuard = _makeSyncCurrencyGuard;

export type OfflineAuthTransitionDeps = {
  auth?: {
    onAuthStateChange?: (cb: (event?: string, session?: {user?: {id?: unknown} | null} | null) => void) => {
      data?: {subscription?: {unsubscribe?: () => void}};
    };
  } | null;
  mountedRef: {current: boolean};
  refreshSeqRef: {current: number};
  activeActorRef: {current: string | null};
  clearLocalState: () => void;
  refresh: () => Promise<void> | void;
  syncNow: () => Promise<void> | void;
  scheduleMacrotask?: (run: () => void) => () => void;
};

export function _bindOfflineAuthTransition(deps: OfflineAuthTransitionDeps): () => void {
  const {
    auth,
    mountedRef,
    refreshSeqRef,
    activeActorRef,
    clearLocalState,
    refresh,
    syncNow,
    scheduleMacrotask = (run) => {
      const timer = setTimeout(run, 0);
      return () => clearTimeout(timer);
    },
  } = deps;
  if (!auth || typeof auth.onAuthStateChange !== 'function') return () => {};
  let cancelPending: (() => void) | null = null;
  let authGen = 0;
  const clearPending = () => {
    const cancel = cancelPending;
    cancelPending = null;
    cancel?.();
  };
  const sub = auth.onAuthStateChange((event, session) => {
    clearPending();
    const gen = ++authGen;
    refreshSeqRef.current += 1;
    const sessionUserId =
      event === 'SIGNED_OUT'
        ? null
        : typeof session?.user?.id === 'string' && session.user.id.trim()
          ? session.user.id.trim()
          : lastAuthenticatedUserId();
    if (!sessionUserId || (activeActorRef.current !== null && activeActorRef.current !== sessionUserId)) {
      activeActorRef.current = null;
      if (mountedRef.current) {
        clearLocalState();
      }
    }
    cancelPending = scheduleMacrotask(() => {
      cancelPending = null;
      if (!mountedRef.current || gen !== authGen) return;
      void refresh();
      if (sessionUserId) {
        void syncNow();
      }
    });
  });
  return () => {
    authGen += 1;
    clearPending();
    sub?.data?.subscription?.unsubscribe?.();
  };
}

export function useOfflineQueueCore(
  formKind: string,
  validateConfig: (kind: string) => unknown,
  drainForActor: (kind: string, actor: string, stillCurrent: () => boolean) => Promise<void>,
) {
  const [stuckRows, setStuckRows] = useState<QueuedSubmission[]>([]);
  const [queuedCount, setQueuedCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const mountedRef = useRef(true);
  const inFlightRef = useRef(false);
  const resyncRequestedRef = useRef(false);
  const refreshSeqRef = useRef(0);
  const activeActorRef = useRef<string | null>(null);

  // Validate the form_kind once at mount — typos blow up loud, not silent.
  useEffect(() => {
    validateConfig(formKind);
  }, [formKind, validateConfig]);

  const refresh = useCallback(async () => {
    const seq = ++refreshSeqRef.current;
    const actor = await safeReadActor(sb);
    if (!mountedRef.current || seq !== refreshSeqRef.current) return;
    if (!actor) {
      activeActorRef.current = null;
      setQueuedCount(0);
      setStuckRows([]);
      return;
    }
    const stillCurrent = makeSyncCurrencyGuard(actor);
    const [queued, stuck] = await Promise.all([
      listQueued(formKind, {actorProfileId: actor, stillCurrent}),
      listStuck(formKind, {actorProfileId: actor, stillCurrent}),
    ]);
    if (!mountedRef.current || seq !== refreshSeqRef.current) return;
    const liveActor = await safeReadActor(sb);
    if (!mountedRef.current || seq !== refreshSeqRef.current) return;
    if (liveActor !== actor || !stillCurrent()) {
      if (!liveActor || (activeActorRef.current !== null && activeActorRef.current !== liveActor)) {
        activeActorRef.current = null;
        setQueuedCount(0);
        setStuckRows([]);
      }
      return;
    }
    activeActorRef.current = actor;
    setQueuedCount(queued.length);
    setStuckRows(stuck);
  }, [formKind]);

  const syncNow = useCallback(async () => {
    if (inFlightRef.current) {
      resyncRequestedRef.current = true;
      return;
    }
    inFlightRef.current = true;
    resyncRequestedRef.current = false;
    if (mountedRef.current) setSyncing(true);
    try {
      const actor = await safeReadActor(sb);
      if (actor) {
        const stillCurrent = makeSyncCurrencyGuard(actor);
        // Recover any rows orphaned in 'syncing' from a prior tab/crash
        // mid-flight for the currently authenticated actor only.
        await recoverStaleSyncing(formKind, {actorProfileId: actor, stillCurrent});
        await drainForActor(formKind, actor, stillCurrent);
      }
    } finally {
      inFlightRef.current = false;
      if (mountedRef.current) setSyncing(false);
      await refresh();
      if (resyncRequestedRef.current && mountedRef.current) {
        resyncRequestedRef.current = false;
        void syncNow();
      }
    }
  }, [formKind, refresh, drainForActor]);

  const verifyStuckOwner = useCallback(
    async (csid: string) => {
      const actor = await safeReadActor(sb);
      if (!actor) return null;
      const stillCurrent = makeSyncCurrencyGuard(actor);
      const row = await getSubmission(csid);
      if (!row || row.form_kind !== formKind || !row.actor_profile_id || row.actor_profile_id !== actor) return null;
      if ((await queuedReportActor(sb)) !== actor || !stillCurrent()) return null;
      return {actor, stillCurrent};
    },
    [formKind],
  );

  const retryStuck = useCallback(
    async (csid: string) => {
      const owned = await verifyStuckOwner(csid);
      if (!owned) {
        await refresh();
        return;
      }
      const retried = await retrySubmission(csid, {actorProfileId: owned.actor, stillCurrent: owned.stillCurrent});
      await refresh();
      if (retried && (await queuedReportActor(sb)) === owned.actor && owned.stillCurrent()) {
        // Fire a sync pass immediately so the operator sees the result.
        await syncNow();
      }
    },
    [verifyStuckOwner, refresh, syncNow],
  );

  const discardStuck = useCallback(
    async (csid: string) => {
      const owned = await verifyStuckOwner(csid);
      if (owned) {
        await discardSubmission(csid, {actorProfileId: owned.actor, stillCurrent: owned.stillCurrent});
      }
      await refresh();
    },
    [verifyStuckOwner, refresh],
  );

  // Mount: load existing stuck/queued state, fire one sync pass.
  useEffect(() => {
    mountedRef.current = true;
    refresh();
    syncNow();
    return () => {
      mountedRef.current = false;
    };
  }, [refresh, syncNow]);

  // Re-scope queued/stuck state and replay eligibility on auth transitions.
  // Never call Supabase APIs inside onAuthStateChange (which runs inside the
  // auth client's exclusive lock): clear local foreign/signed-out state
  // synchronously and defer refresh/syncNow to a single-slot cancellable macrotask.
  useEffect(() => {
    const auth = (
      sb as {
        auth?: OfflineAuthTransitionDeps['auth'];
      }
    ).auth;
    return _bindOfflineAuthTransition({
      auth,
      mountedRef,
      refreshSeqRef,
      activeActorRef,
      clearLocalState: () => {
        setQueuedCount(0);
        setStuckRows([]);
      },
      refresh,
      syncNow,
    });
  }, [refresh, syncNow]);

  // Background triggers: online event + 60s tick.
  useEffect(() => {
    function handleOnline() {
      syncNow();
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('online', handleOnline);
    }
    const id = setInterval(() => {
      // navigator.onLine is advisory, not authoritative — fire sync regardless;
      // the verified RPC result itself is the source of truth.
      syncNow();
    }, TICK_INTERVAL_MS);
    return () => {
      if (typeof window !== 'undefined') window.removeEventListener('online', handleOnline);
      clearInterval(id);
    };
  }, [syncNow]);

  return {stuckRows, queuedCount, refresh, retryStuck, discardStuck, syncNow, syncing};
}

async function drainFlatFormForActor(formKind: string, actor: string, stillCurrent: () => boolean): Promise<void> {
  if ((await queuedReportActor(sb)) === actor && stillCurrent()) {
    await _drainQueuedFormKind(formKind, sb);
  }
}

/**
 * formKind — registry key (e.g. 'fuel_supply').
 */
export function useOfflineSubmit(formKind: string): OfflineSubmitApi {
  const core = useOfflineQueueCore(formKind, getFormConfig, drainFlatFormForActor);
  const {refresh} = core;

  const submit = useCallback(
    async (payload: FormPayload, opts: SubmitOptions = {}) => {
      return _runSubmit({formKind, payload, opts, refresh});
    },
    [formKind, refresh],
  );

  return {
    submit,
    ...core,
  };
}

// ============================================================================
// Test-exported seams (mirrors useOfflineRpcSubmit pattern)
// ============================================================================
export const _classifyError = classifyError;
export const _classifyStorageError = classifyStorageError;

// ----------------------------------------------------------------------------
// _runSubmit — the submit() lifecycle without React. Test seam.
// ----------------------------------------------------------------------------
//
// Three return states (Phase 1D-A — Codex review v2.1 correction 4):
//   - 'synced' : server positively verified the committed request receipt
//   - 'queued' : transient failure (network/5xx/unknown). Will replay later.
//   - 'stuck'  : deterministic failure (storage 401/403, row schema after
//                photo upload, etc.). Operator surfaces in stuck modal.
//
// Empty-photos short-circuit (Codex review v2.1 correction 7):
//   When formKind has hasPhotos:true but payload.photos is empty/absent,
//   the lifecycle falls through to the no-photo flat path so existing
//   pig_dailys_offline.spec.js cases stay green.
export async function _runSubmit({
  formKind,
  payload,
  opts = {},
  refresh,
}: {
  formKind: string;
  payload: FormPayload;
  opts?: SubmitOptions;
  refresh?: (() => Promise<void>) | null;
}): Promise<SubmitResult> {
  const cfg = getFormConfig(formKind);
  const csid = opts?.csid ?? newClientSubmissionId();
  const id = opts?.id ?? `${formKind}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const liveActor = await queuedReportActor(sb);
  const actorProfileId = opts?.expectedActor ?? liveActor;
  if (!actorProfileId) throw new Error('Sign in before saving this report. Your answers are still on this form.');
  if (opts?.expectedActor && liveActor !== opts.expectedActor) {
    throw new Error('Sign in before saving this report. The signed-in account changed.');
  }
  if (opts?.assertCurrent) {
    const ok = await opts.assertCurrent();
    if (!ok) throw new Error('Sign in before saving this report. The signed-in account changed.');
  }

  // hasPhotos branch only fires if hasPhotos:true AND payload.photos has
  // at least 1 file. Empty/absent → flat path.
  //
  // The list is named as the File/Blob array this branch is documented and
  // gated to carry. It is NOT re-checked element by element here on purpose:
  // preparePhotos owns that validation and THROWS on a non-file entry, and
  // that throw is the deterministic operator error this path surfaces inline.
  // Filtering here would swallow it and submit a report with photos missing.
  const rawPhotos: unknown = payload ? payload.photos : undefined;
  const photoFiles: Blob[] = cfg.hasPhotos && Array.isArray(rawPhotos) ? rawPhotos : [];
  if (opts?.replacesCsid) {
    const stillCurrent = makeSyncCurrencyGuard(actorProfileId);
    const prepared = photoFiles.length ? await preparePhotos(formKind, csid, photoFiles) : [];
    const sanitized = sanitizePhotosFromPayload(payload, await queuedReportPhotoMetadata(prepared));
    const record = buildRecord(formKind, sanitized, {id, csid});
    if ((await queuedReportActor(sb)) !== actorProfileId || !stillCurrent()) {
      throw new Error('Sign in before saving this report. The signed-in account changed.');
    }
    // Persist/link before any write so reopening the original follows this
    // pending replacement rather than minting another submission.
    await enqueueSubmissionWithPhotos({
      formKind,
      csid,
      payload: sanitized,
      record,
      photos: prepared,
      actorProfileId,
      replacesCsid: opts.replacesCsid,
      stillCurrent,
    });
    const entry = await getSubmission(csid);
    if (!entry) throw new Error('Could not preserve the replacement report.');
    await _syncQueuedEntry(formKind, entry, sb, opts?.assertCurrent);
    const remaining = await getSubmission(csid);
    if (refresh) await refresh();
    return {state: !remaining ? 'synced' : remaining.status === 'failed' ? 'stuck' : 'queued', csid, id, record};
  }
  if (photoFiles.length > 0) {
    return await _runSubmitWithPhotos({
      formKind,
      payload,
      photoFiles,
      csid,
      id,
      refresh,
      actorProfileId,
      assertCurrent: opts?.assertCurrent,
    });
  }

  return await _runFlatSubmit({
    formKind,
    payload,
    csid,
    id,
    refresh,
    actorProfileId,
    assertCurrent: opts?.assertCurrent,
  });
}

// The arguments every lifecycle branch below shares, once the identities have
// been minted.
type SubmitRun = {
  formKind: string;
  payload: FormPayload;
  csid: string;
  id: string;
  actorProfileId: string;
  refresh?: (() => Promise<void>) | null;
  assertCurrent?: () => Promise<boolean> | boolean;
};

type IdentityAbortError = Error & {_identityAbort?: boolean};

function identityChangedError(): IdentityAbortError {
  const err: IdentityAbortError = new Error('Sign in before saving this report. The signed-in account changed.');
  err._identityAbort = true;
  return err;
}

function isIdentityAbortError(err: unknown): boolean {
  return isPlainRecord(err) && err._identityAbort === true;
}

// ----------------------------------------------------------------------------
// Flat-row submit (no-photo path, verified receipt required)
// ----------------------------------------------------------------------------
async function _runFlatSubmit({
  formKind,
  payload,
  csid,
  id,
  refresh,
  actorProfileId,
  assertCurrent,
}: SubmitRun): Promise<SubmitResult> {
  // Build the row WITHOUT raw photo files (caller may have passed them; the
  // hasPhotos branch handles those above). buildRecord already coerces to
  // photos:[] when payload.photos is empty/absent.
  const sanitized = sanitizePhotosFromPayload(payload, []);
  const record = buildRecord(formKind, sanitized, {id, csid});
  const stillCurrent = makeSyncCurrencyGuard(actorProfileId);

  try {
    if ((await queuedReportActor(sb)) !== actorProfileId || !stillCurrent()) {
      throw identityChangedError();
    }
    if (assertCurrent) {
      const ok = await assertCurrent();
      if (!ok) {
        throw identityChangedError();
      }
    }
    const {error} = await attemptInsert(formKind, record, actorProfileId);
    if (!error) {
      return {state: 'synced', csid, id, record};
    }
    if (
      isDuplicateCsidViolation(error) ||
      isDailySupersededViolation(error) ||
      error.code === 'WCF01' ||
      error.code === QUEUED_REPORT_CONFLICT
    ) {
      await enqueueSubmission({formKind, csid, payload: sanitized, record, actorProfileId});
      await markStuckNow(csid, 'Needs Help: ' + (error.message || dailySupersededMessage(record)), {
        actorProfileId,
      });
      if (refresh) await refresh();
      return {state: 'stuck', csid, id, record, error};
    }
    const kind = classifyError(error);
    if (kind === 'schema') {
      throw new Error(`offlineSubmit: schema/validation error: ${error.message ?? error.code ?? 'unknown'}`);
    }
    await enqueueSubmission({formKind, csid, payload: sanitized, record, actorProfileId});
    await markFailed(csid, error.message ?? `${kind} error`, {actorProfileId});
    if (refresh) await refresh();
    return {state: 'queued', csid, id, record};
  } catch (err) {
    if (isIdentityAbortError(err)) {
      throw err;
    }
    const kind = classifyError(err);
    if (kind === 'schema') {
      throw err;
    }
    await enqueueSubmission({formKind, csid, payload: sanitized, record, actorProfileId});
    await markFailed(csid, errMessage(err), {actorProfileId});
    if (refresh) await refresh();
    return {state: 'queued', csid, id, record};
  }
}

// ----------------------------------------------------------------------------
// hasPhotos submit lifecycle (Codex review v2.1 corrections 1+2+3+4)
// ----------------------------------------------------------------------------
//
// Memory-only preparation, atomic enqueue on any queue/stuck path, and
// state:'stuck' return for deterministic failures (storage 401/403, row
// schema-after-upload).
//
// Online happy path produces NO IDB writes. Tab death pre-upload loses the
// in-memory blobs — same loss profile as a no-photo submit dying mid-attempt.
async function _runSubmitWithPhotos({
  formKind,
  payload,
  photoFiles,
  csid,
  id,
  refresh,
  actorProfileId,
  assertCurrent,
}: SubmitRun & {photoFiles: Blob[]}): Promise<SubmitResult> {
  const stillCurrent = makeSyncCurrencyGuard(actorProfileId);
  // Step 1: prepare (compress + path/captured_at) ONCE in memory.
  // preparePhotos errors (RangeError on >10, TypeError on bad input,
  // compressImage decode failures) are deterministic operator errors.
  // Let them throw inline — nothing has hit IDB or storage yet, no cleanup
  // needed. Caller's try/catch surfaces inline.
  const prepared = await preparePhotos(formKind, csid, photoFiles);

  // Step 2: build the daily-row record with sanitized photo metadata.
  const photoMeta = await queuedReportPhotoMetadata(prepared);
  const sanitized = sanitizePhotosFromPayload(payload, photoMeta);
  const record = buildRecord(formKind, sanitized, {id, csid});

  if ((await queuedReportActor(sb)) !== actorProfileId || !stillCurrent()) {
    throw identityChangedError();
  }
  if (assertCurrent) {
    const ok = await assertCurrent();
    if (!ok) {
      throw identityChangedError();
    }
  }

  const persistPhotosOutcome = async (
    state: 'stuck' | 'queued',
    lastError: string,
    error: unknown,
  ): Promise<SubmitResult> => {
    await enqueueSubmissionWithPhotos({
      csid,
      formKind,
      payload: sanitized,
      record,
      photos: prepared,
      actorProfileId,
      status: state === 'stuck' ? 'failed' : 'queued',
      retryCount: state === 'stuck' ? MAX_RETRIES : 1,
      lastError,
    });
    if (refresh) await refresh();
    return {state, csid, id, record, error};
  };

  // Step 3: try uploads online, sequential.
  let uploadsCompleted = false;
  try {
    await uploadPreparedPhotosSequential(sb, prepared, {
      upsert: false,
      assertCurrent: async () => {
        if ((await queuedReportActor(sb)) !== actorProfileId || !stillCurrent()) return false;
        return assertCurrent ? await assertCurrent() : true;
      },
    });
    uploadsCompleted = true;
  } catch (uploadErr) {
    const kind = classifyStorageError(uploadErr);
    if (kind === 'rls-stuck' || kind === 'schema') {
      // Codex review v2.1 correction 4: surface loudly via stuck modal.
      // Atomic enqueue with stuck status so the operator can retry after
      // a policy/code fix.
      return await persistPhotosOutcome('stuck', storageErrSummary(uploadErr), uploadErr);
    }
    // network / server / unknown / success-409-but-thrown(?) → queue + retry.
    return await persistPhotosOutcome('queued', storageErrSummary(uploadErr), uploadErr);
  }

  // Step 4: row insert. Reached only when all photos uploaded.
  void uploadsCompleted;
  try {
    if ((await queuedReportActor(sb)) !== actorProfileId || !stillCurrent()) {
      throw new Error('The signed-in account changed during submission. Operation aborted; your edits were kept.');
    }
    if (assertCurrent) {
      const ok = await assertCurrent();
      if (!ok) {
        throw new Error('The signed-in account changed during submission. Operation aborted; your edits were kept.');
      }
    }
    const {error} = await attemptInsert(formKind, record, actorProfileId);
    if (!error) {
      // Synced happy path. Nothing in IDB to clean up — we never wrote.
      return {state: 'synced', csid, id, record};
    }
    if (isDailySupersededViolation(error)) {
      // Superseded by an existing active report. Re-raise as schema-class so
      // the catch below routes it to STUCK with the friendly message (photos
      // are already uploaded; the operator can discard the redundant row).
      throw dailySupersededError(record);
    }
    const kind = classifyError(error);
    if (kind === 'schema') {
      // Codex review v2.1 correction 3: schema-after-photo-upload goes to
      // STUCK (not throw). Photos are already in the bucket; queueing
      // preserves recovery after a code/schema fix.
      return await persistPhotosOutcome('stuck', error.message ?? String(error.code ?? 'schema error'), error);
    }
    // network / server / 401/403 / unknown → queue + retry.
    return await persistPhotosOutcome('queued', error.message ?? `${kind} error`, error);
  } catch (err) {
    const kind = classifyError(err);
    if (kind === 'schema') {
      // Same as the error-envelope schema branch — go to stuck, don't throw.
      return await persistPhotosOutcome('stuck', errMessage(err), err);
    }
    return await persistPhotosOutcome('queued', errMessage(err), err);
  }
}

function sanitizePhotosFromPayload(payload: FormPayload, photoMeta: unknown): FormPayload {
  if (!payload) return {photos: photoMeta};
  const next: Record<string, unknown> = {...payload};
  next.photos = photoMeta;
  return next;
}

function storageErrSummary(err: unknown): string {
  if (!err) return 'storage upload failed';
  if (err instanceof StorageUploadError) {
    return `${err.message} (status=${err.status ?? '?'} code=${err.code ?? '?'} key=${err.photo_key ?? '?'})`;
  }
  const e = isPlainRecord(err) ? err : null;
  return e && e.message != null ? String(e.message) : String(err);
}

// ----------------------------------------------------------------------------
// _syncQueuedEntry — drain ONE queued entry. Test seam (Codex correction 8).
// ----------------------------------------------------------------------------
//
// Verify the queued actor before touching Storage or a create owner. Photo
// count, paths and byte digests must match the frozen request. Storage 409
// permits continuing; only the positive server receipt acknowledges the queue.
// Empty-photo reports use the same receipt boundary without a Storage call.
export async function _syncQueuedEntry(
  formKind: string,
  entry: QueuedSubmission,
  sbClient: SupabaseClient = sb,
  assertCurrent?: () => Promise<boolean> | boolean,
): Promise<void> {
  // Legacy work and a different login must be preserved untouched rather than
  // deleted or marked failed; neither may upload photos or invoke a writer
  // under the wrong identity.
  if (!entry.actor_profile_id) {
    return;
  }
  const expectedActor = entry.actor_profile_id;
  const stillCurrent = makeSyncCurrencyGuard(expectedActor);
  if (!stillCurrent()) {
    return;
  }
  let currentActor: string | null;
  try {
    currentActor = await queuedReportActor(sbClient);
  } catch {
    return;
  }
  if (!currentActor || currentActor !== expectedActor || !stillCurrent()) {
    return;
  }
  const scope = {actorProfileId: expectedActor, stillCurrent};
  if (entry.replacement_csid) {
    await markStuckNow(entry.csid, 'A replacement is pending. Open it to continue.', scope);
    return;
  }
  const previousLastAttemptAt = entry.last_attempt_at ?? null;
  const marked = await markSyncing(entry.csid, scope);
  if (!marked) return;

  const isLiveOwnerCurrent = async (): Promise<boolean> => {
    if (!stillCurrent()) return false;
    try {
      return (await queuedReportActor(sbClient)) === expectedActor && stillCurrent();
    } catch {
      return false;
    }
  };

  // `entry.record` came back from IndexedDB, so its shape is re-proved here.
  // A record that is not an object, or whose `photos` is not an array, yields
  // the same empty list the old `entry.record && entry.record.photos` guard
  // produced, and the entry drains down the no-photo path.
  const storedRecord = entry.record;
  const recordPhotos: unknown[] =
    isPlainRecord(storedRecord) && Array.isArray(storedRecord.photos) ? storedRecord.photos : [];
  const cfg = getFormConfig(formKind);
  const expectsPhotos = cfg.hasPhotos && recordPhotos.length > 0;

  if (expectsPhotos) {
    // Codex correction 5: replay guard. Confirm photo_blobs exists for every
    // path in record.photos before touching storage or the row.
    const blobs = await listPhotoBlobsByCsid(entry.csid);
    if (!(await isLiveOwnerCurrent())) {
      await restoreSubmissionQueued(entry.csid, {actorProfileId: expectedActor}, previousLastAttemptAt);
      return;
    }
    if (blobs.length !== recordPhotos.length) {
      await markStuckNow(entry.csid, 'photo_blobs missing or incomplete (count mismatch)', scope);
      return;
    }
    const blobPaths = new Map(blobs.map((b) => [b.key, b]));
    const checkedPaths = new Set<string>();
    for (const meta of recordPhotos) {
      // Each stored photo-meta entry is data too. A row whose `path` is not a
      // string can never match a blob key, which is exactly the missing-path
      // stuck branch below — so it takes it, rather than comparing undefined.
      const metaPath = isPlainRecord(meta) && typeof meta.path === 'string' ? meta.path : undefined;
      if (metaPath === undefined || !blobPaths.has(metaPath) || checkedPaths.has(metaPath)) {
        if (!(await isLiveOwnerCurrent())) {
          await restoreSubmissionQueued(entry.csid, {actorProfileId: expectedActor}, previousLastAttemptAt);
          return;
        }
        await markStuckNow(entry.csid, `photo_blobs missing for path ${String(metaPath)}`, scope);
        return;
      }
      checkedPaths.add(metaPath);
      const digest = isPlainRecord(meta) && typeof meta.sha256 === 'string' ? meta.sha256 : '';
      if (!/^[a-f0-9]{64}$/.test(digest) || (await photoDigest(blobPaths.get(metaPath)!.blob)) !== digest) {
        if (!(await isLiveOwnerCurrent())) {
          await restoreSubmissionQueued(entry.csid, {actorProfileId: expectedActor}, previousLastAttemptAt);
          return;
        }
        await markStuckNow(entry.csid, 'Queued photo bytes could not be verified. Open this report to review.', scope);
        return;
      }
    }
    // Sequential upload with upsert:false. RLS on `daily-photos` grants
    // authenticated INSERT only (mig 099 daily_photos_auth_insert; the dead
    // anon INSERT policy was dropped by mig 109) — there is NO UPDATE policy.
    // upsert:true would trigger an UPSERT (INSERT-OR-UPDATE) and
    // fail 403 RLS even on a fresh INSERT path because supabase-storage's
    // upsert path checks the UPDATE policy too. With upsert:false, a fresh
    // path INSERTs cleanly; an already-existing path 409s, which the
    // classifier treats as 'success-continue'. The retained local bytes are
    // checked against the request digest above. A Storage conflict alone is
    // never proof that the report saved; the server receipt is still required.
    for (const blob of blobs) {
      if (!(await isLiveOwnerCurrent())) {
        await restoreSubmissionQueued(entry.csid, {actorProfileId: expectedActor}, previousLastAttemptAt);
        return;
      }
      if (assertCurrent) {
        const ok = await assertCurrent();
        if (!ok) {
          if (await isLiveOwnerCurrent()) {
            await markFailed(entry.csid, 'Identity changed during sync', scope);
          } else {
            await restoreSubmissionQueued(entry.csid, {actorProfileId: expectedActor}, previousLastAttemptAt);
          }
          return;
        }
      }
      try {
        const {error} = await sbClient.storage
          .from('daily-photos')
          .upload(blob.key, blob.blob, {upsert: false, contentType: blob.mime ?? 'image/jpeg'});
        if (error) {
          if (!(await isLiveOwnerCurrent())) {
            await restoreSubmissionQueued(entry.csid, {actorProfileId: expectedActor}, previousLastAttemptAt);
            return;
          }
          // supabase-storage's declared error type carries only `message`; the
          // status and provider code arrive on the runtime shape (`statusCode`
          // / `error`) that the wrapper exists to normalize. Reading them
          // through one narrowing keeps that normalization visible instead of
          // asserting fields onto the library's type.
          const raw: Record<string, unknown> = isPlainRecord(error) ? error : {};
          const wrapped = new StorageUploadError({
            message: error.message ?? 'storage upload failed',
            status: raw.statusCode != null ? Number(raw.statusCode) : raw.status,
            code: raw.error ?? raw.code,
            path: blob.key,
            photo_key: blob.photo_key,
            cause: error,
          });
          const kind = classifyStorageError(wrapped);
          if (kind === 'success-409') continue;
          if (kind === 'rls-stuck' || kind === 'schema') {
            await markStuckNow(entry.csid, storageErrSummary(wrapped), scope);
            return;
          }
          await markFailed(entry.csid, storageErrSummary(wrapped), scope);
          return;
        }
      } catch (err) {
        if (!(await isLiveOwnerCurrent())) {
          await restoreSubmissionQueued(entry.csid, {actorProfileId: expectedActor}, previousLastAttemptAt);
          return;
        }
        const kind = classifyStorageError(err);
        if (kind === 'rls-stuck' || kind === 'schema') {
          await markStuckNow(entry.csid, storageErrSummary(err), scope);
          return;
        }
        await markFailed(entry.csid, storageErrSummary(err), scope);
        return;
      }
    }
  }

  // Insert the row. Same classifier as the flat path.
  //
  // `entry.record` is passed through EXACTLY as the JS did, cast rather than
  // narrowed. The conversion had substituted `{}` for a non-object record,
  // which is a runtime change on the offline write path: a malformed queued
  // row would have been sent as an empty insert instead of as itself. That may
  // well be the better behaviour, but it is a product decision and not one a
  // type annotation gets to make silently — so the cast preserves today's
  // behaviour and the change can be proposed on its own merits.
  try {
    if (!(await isLiveOwnerCurrent())) {
      await restoreSubmissionQueued(entry.csid, {actorProfileId: expectedActor}, previousLastAttemptAt);
      return;
    }
    if (assertCurrent) {
      const ok = await assertCurrent();
      if (!ok) {
        if (await isLiveOwnerCurrent()) {
          await markFailed(entry.csid, 'Identity changed during sync', scope);
        } else {
          await restoreSubmissionQueued(entry.csid, {actorProfileId: expectedActor}, previousLastAttemptAt);
        }
        return;
      }
    }
    const {error} = await writeRow(formKind, entry.record as FormRecord, expectedActor, sbClient);
    if (error) {
      if (!(await isLiveOwnerCurrent())) {
        await restoreSubmissionQueued(entry.csid, {actorProfileId: expectedActor}, previousLastAttemptAt);
        return;
      }
      const kind = classifyError(error);
      if (kind === 'schema') {
        await markStuckNow(entry.csid, error.message ?? 'schema error', scope);
        return;
      }
      await markFailed(entry.csid, error.message ?? `${kind} error`, scope);
      return;
    }
    if (!(await isLiveOwnerCurrent())) {
      await restoreSubmissionQueued(entry.csid, {actorProfileId: expectedActor}, previousLastAttemptAt);
      return;
    }
    await acknowledgeQueuedReport(entry.csid, expectedActor, {stillCurrent});
  } catch (err) {
    if (!(await isLiveOwnerCurrent())) {
      await restoreSubmissionQueued(entry.csid, {actorProfileId: expectedActor}, previousLastAttemptAt);
      return;
    }
    if (classifyError(err) === 'schema') await markStuckNow(entry.csid, errMessage(err), scope);
    else await markFailed(entry.csid, errMessage(err), scope);
  }
}

// ----------------------------------------------------------------------------
// _drainQueuedFormKind — drain ALL queued entries for a form-kind. Test seam.
// ----------------------------------------------------------------------------
export async function _drainQueuedFormKind(formKind: string, sbClient: SupabaseClient = sb): Promise<void> {
  let actor: string | null = null;
  try {
    actor = await queuedReportActor(sbClient);
  } catch {
    return;
  }
  if (!actor) return;
  const stillCurrent = makeSyncCurrencyGuard(actor);
  const queued = await listQueued(formKind, {actorProfileId: actor, stillCurrent});
  for (const entry of queued) {
    let liveActor: string | null = null;
    try {
      liveActor = await queuedReportActor(sbClient);
    } catch {
      return;
    }
    if (liveActor !== actor || !stillCurrent()) return;
    await _syncQueuedEntry(formKind, entry, sbClient);
  }
}
